import React, { useEffect, useMemo, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { BillActions, paidStatusToIcon, documentStatusToIcon, documentTypeToIcon, paidStatusToColor, insuranceClaimStatusToIcon, insuranceClaimStatusToColor } from '.';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { formatCurrency, fullDateFromPostgresString, getHealthIDLabel, stringDatePostgresToDate } from '../../../utils/index.jsx';
import SearchBox, { FilterItem } from '../../../components/general/SearchBox';
import { BillStatus } from '../Request/types';
import { Grid, Paper, Typography, TextField, FormControl, Button, InputLabel, Select, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { ColourButton } from '../../../components/general/mini_components-ts';
import { ChipContainer } from '../Request/RequestInfo';
import { ColourChip } from '../../../components/general/mini_components-ts';
import { DocumentStatus, DocumentType } from './types';
import { HOSPITAL_BILLING_VIEW_DOCUMENT } from '../../../routes/urls';
import { useHistory } from 'react-router-dom';
import { documentStatusToColor, documentTypeToColor } from '../../../utils/bill';
import { IInsurance, ReduxStore } from '../../../constants/types';
import { LocalizeContextProps } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { InsuranceClaimStatus } from '../InsuranceClaims/types';
import { INSURANCE_CLAIM_STATUS_OPTIONS } from '../InsuranceClaims/fields';

type BillsTableProps = {
    bills: any[];
    patients: any[];
    insurances: IInsurance[];
    languageCode: string;
    hideInsuranceFilter?: boolean;
    hideInsuranceStatusFilter?: boolean;
    currency: string;
    hasBudgets: boolean;
    hideSearchPatient?: boolean;
    canCreateBugdet: boolean;
    makeActionBillCallBack: (index:number, action:BillActions) => void;
};

const BillsTable: React.FC<BillsTableProps> = ({ bills, patients, languageCode, currency, hideInsuranceFilter, hideInsuranceStatusFilter, hideSearchPatient, insurances, canCreateBugdet, 
                                                    hasBudgets, makeActionBillCallBack }) => {
    const [patientName, setPatientName] = useState<string>("");
    const [statusPaidFilter, setStatusPaidFilter] = useState<BillStatus[]>([]);
    const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatus[]>([]);
    const [typeFilter, setTypeFilter] = useState<DocumentType[]>([]);
    const [insuranceStatusFilter, setInsuranceStatusFilter] = useState<InsuranceClaimStatus[]>([]);
    const [selectedInsurance, setSelectedInsurance] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [currentPageTable, setCurrentPageTable] = useState<number>(0);
  
    const history = useHistory();

    useEffect(() => {
        setCurrentPageTable(0);
    }, [patientName, statusPaidFilter, documentStatusFilter, typeFilter, selectedInsurance, insuranceStatusFilter, startDate, endDate]);

    // Get unique insurance IDs from patients
    const uniqueInsurance:IInsurance[] = useMemo(() => {
        const insuranceMap = new Map<string, IInsurance>();
        bills.forEach(bill => {
            if (bill.insuranceCode && insurances && !insuranceMap.has(bill.insuranceCode)) {
                const insurance = insurances.find((insurance) => insurance.code === bill.insuranceCode);
                if(insurance){
                    insuranceMap.set(bill.insuranceCode, insurance);
                }
            }
        });
        return Array.from(insuranceMap.values());
    }, [bills, insurances]);

    // Create options from insurance data
    const insuranceOptions = useMemo(() => {
        const options: { value: string; label: string; }[] = [];
        uniqueInsurance.forEach((insurance, index) => {
            
            options.push({
                value: insurance.code,
                label: insurance.name
            });
            
        });
        return options;
    }, [uniqueInsurance]);

    function changePageCallback(currentPage:number){
        setCurrentPageTable(currentPage);
    }

    const billsPatients = useMemo(() => {
        // Sort bills by createdAt descending (newest first)
        const sortedBills = [...bills].sort((a, b) => 
            stringDatePostgresToDate(b.createdAt).getTime() - stringDatePostgresToDate(a.createdAt).getTime()
        );
        return sortedBills.map((bill, indexBill) => {
            console.log(bill);
            const patient = patients.find((patient) => patient.uuid === bill.uuidPatient);
            const totalPatient = bill.totalGross - bill.totalDiscount;
            const externalPaid = (bill.insuranceClaims || []).reduce((acc: number, claim: any) => acc + claim.requestedAmount, 0);
            const isPaid = (totalPatient - bill.totalPaid - externalPaid) <= 0;
            
            // Get insurance claim statuses for this bill
            const insuranceClaimStatuses = bill.insuranceClaims?.map((claim: any) => claim.status) || [];
            
            return {
                "idInvestigation" : bill.documentNumber ? bill.documentNumber.toString() : "",
                "id" : indexBill,
                "uuid" : bill.uuid,
                "uuidPatient": bill.uuidPatient,
                "type" : bill.type,
                "insuranceCode" : bill.insuranceCode,
                "health_id" : patient?.personalData.automated_health_id ? patient?.personalData.automated_health_id : patient?.personalData.health_id,
                "patientName" :patient?.personalData.name+" "+patient?.personalData.surnames, 
                "patient" : <><ColourButton size='small' icon={documentTypeToIcon(bill.type)} rgbcolor={documentTypeToColor(bill.type)} label={null}/> {patient?.personalData.name+" "+patient?.personalData.surnames}</>, 
                "total" : formatCurrency(totalPatient, currency, languageCode),
                "status" : renderStatus(bill.status, bill.type, hasBudgets, isPaid ? BillStatus.PAID : BillStatus.PENDING_PAYMENT),
                "statusValue" : bill.status,
                "paidStatus" : (bill.type === DocumentType.INVOICE && bill.status === DocumentStatus.CLOSED) ? isPaid ? BillStatus.PAID : BillStatus.PENDING_PAYMENT : null,
                "insuranceClaimStatuses" : insuranceClaimStatuses,
                "dateCreation" : fullDateFromPostgresString(languageCode, bill.createdAt),
                "dateCreationRaw" : stringDatePostgresToDate(bill.createdAt)
            }
        })}, [bills, patients, languageCode]);
    
    function callbackNameTyped(name:string){
        setPatientName(name);        
    }

    function renderStatus(billStatus:DocumentStatus, billType:DocumentType, hasBudgets:boolean, paidStatus:BillStatus){
        let listChips = [];
        if(billStatus === DocumentStatus.DRAFT){
            listChips.push(<ColourButton icon={documentStatusToIcon(billStatus)} rgbcolor={documentStatusToColor(billStatus)} label=""/>);
        }
        else{
            listChips.push(<ColourButton icon={documentStatusToIcon(billStatus)} rgbcolor={documentStatusToColor(billStatus)} label="" />);
        }
        
        if(billStatus === DocumentStatus.CLOSED && billType === DocumentType.INVOICE){
            listChips.push(<ColourButton icon={paidStatusToIcon(paidStatus)} rgbcolor={paidStatusToColor(paidStatus)} label=""  /> );
        }
        
        return listChips;
    }
    function renderCore(){
        const rows = billsPatients.filter((bill) => {
            let shouldBeFiltered = patientName.length === 0;
            const activeNameFilter = patientName.length > 0;
            let matchNameFilter = false;
            if(activeNameFilter){
                matchNameFilter = bill.patientName.toLocaleLowerCase().includes(patientName.toLocaleLowerCase()) || bill.health_id?.toLocaleLowerCase().includes(patientName.toLocaleLowerCase());
                shouldBeFiltered = matchNameFilter;
            }
            if(statusPaidFilter.length > 0){
                shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && (bill.paidStatus !== null ? statusPaidFilter.includes(bill.paidStatus) : false) : (bill.paidStatus !== null ? statusPaidFilter.includes(bill.paidStatus) : false));
            }
            if(typeFilter.length > 0){
                shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && typeFilter.includes(bill.type) : typeFilter.includes(bill.type));
            }
            if(documentStatusFilter.length > 0){
                shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && documentStatusFilter.includes(bill.statusValue) : documentStatusFilter.includes(bill.statusValue));
            }
            if(selectedInsurance) {
                shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && bill.insuranceCode === selectedInsurance : bill.insuranceCode === selectedInsurance);
            }
            if(insuranceStatusFilter.length > 0){
                // Check if any insurance claim status matches the filter
                const hasMatchingInsuranceStatus = bill.insuranceClaimStatuses && bill.insuranceClaimStatuses.some((status: InsuranceClaimStatus) => 
                    insuranceStatusFilter.includes(status)
                );
                shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && hasMatchingInsuranceStatus : hasMatchingInsuranceStatus);
            }
            
            // Date range filtering
            if (startDate || endDate) {
                const billDate = bill.dateCreationRaw;
                const startDateTime = startDate ? new Date(startDate + 'T00:00:00') : null;
                const endDateTime = endDate ? new Date(endDate + 'T23:59:59') : null;
                
                if (startDateTime && endDateTime) {
                    shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && billDate >= startDateTime && billDate <= endDateTime : billDate >= startDateTime && billDate <= endDateTime);
                } else if (startDateTime) {
                    shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && billDate >= startDateTime : billDate >= startDateTime);
                } else if (endDateTime) {
                    shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && billDate <= endDateTime : billDate <= endDateTime);
                }
            }
            
            return shouldBeFiltered;
        })
        const healthIdLabel = getHealthIDLabel(patients[0]);
        const headCells = [
                { id: "idInvestigation", alignment: "left", label: <Translate id={`hospital.billing.bill.bill_id`} /> },
                { id: "health_id", alignment: "left", label: <Translate id={healthIdLabel} /> },
                { id: "patient", alignment: "left", label: <Translate id={`hospital.billing.bill.patient`} /> },
                { id: "total", alignment: "left", label: [<Translate id={`hospital.billing.bill.total`} />,"("+currency+")"] },
                { id: "status", alignment: "left", label: [<Translate id={`hospital.billing.bill.status.label`} />]},
                { id: "dateCreation" , alignment: "right",  label: [<Translate id={`hospital.billing.bill.date`} />] } 
        ];

        return <EnhancedTable noHeader headCells={headCells} rows={rows}  noSelectable
                    selectRow={
                        (index:number) => {
                            const findBill = rows.find((bill) => bill.id === index);
                            if(findBill){
                                history.push(HOSPITAL_BILLING_VIEW_DOCUMENT.replace(":uuidDocument", findBill.uuid))}
                            }
                        } 
                        currentPage={currentPageTable} 
                        changePageCallback={changePageCallback}
                        actions={[{"type" : "pdf", "check" :(bill:any) => bill.statusValue === DocumentStatus.CLOSED, 
                                    "func" : (index:number) => {
                                        const findBill = rows.find((bill) => bill.id === index);
                                        if(findBill){
                                            makeActionBillCallBack(findBill.uuid, BillActions.PREVIEW)
                                        }
                                    }
                            }]} 
                            />
    }

    function applyFilterGeneral(filter:number[] , functionAdd: (value:number[]) => void, value:number){
        if(filter && filter.includes(value)){
            if(filter.length === 1){
                functionAdd([]);
            }
            else{
                functionAdd(filter.filter((s) => s !== value));
            }
        }
        else if(filter){
            functionAdd([...filter, value]);
        }
        else{
            functionAdd([value]);
        }
    }

    function applyDocumentStatus(status:DocumentStatus){
        applyFilterGeneral(documentStatusFilter, setDocumentStatusFilter, status);
    }

    function applyTypeStatusFilter(type:DocumentType){
        applyFilterGeneral(typeFilter, setTypeFilter, type);
    }

    function applyStatusPaidFilter(status:BillStatus){
        applyFilterGeneral(statusPaidFilter, setStatusPaidFilter, status);
    }

    function applyInsuranceStatusFilter(status:InsuranceClaimStatus){
        if(insuranceStatusFilter && insuranceStatusFilter.includes(status)){
            if(insuranceStatusFilter.length === 1){
                setInsuranceStatusFilter([]);
            }
            else{
                setInsuranceStatusFilter(insuranceStatusFilter.filter((s) => s !== status));
            }
        }
        else if(insuranceStatusFilter){
            setInsuranceStatusFilter([...insuranceStatusFilter, status]);
        }
        else{
            setInsuranceStatusFilter([status]);
        }
    }

    // Insurance and Date filters component - all on the same line
    const renderDateFilters = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Insurance Select */}
            {!hideInsuranceFilter && insuranceOptions.length > 0 && (
                <FormControl variant="outlined" margin="dense" style={{ minWidth: '200px', maxWidth: '220px' }}>
                    <InputLabel id="insurance-filter-label">
                        <Translate id="investigation.create.personal_data.fields.insurance" />
                    </InputLabel>
                    <Select
                        labelId="insurance-filter-label"
                        value={selectedInsurance}
                        onChange={(e) => setSelectedInsurance(e.target.value)}
                        label={<Translate id="investigation.create.personal_data.fields.insurance" />}
                    >
                        <MenuItem value="">
                            <Translate id="hospital.billing.all_insurances" />
                        </MenuItem>
                        {insuranceOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            {/* Start Date */}
            <FormControl variant="outlined" margin="dense" style={{ maxWidth: '220px' }}>
                <DatePicker
                    label={<Translate id="hospital.search_box.start_date" />}
                    value={startDate ? dayjs(startDate) : null}
                    onChange={(newDate) => setStartDate(newDate ? newDate.format('YYYY-MM-DD') : '')}
                    slotProps={{
                        textField: {
                            variant: 'outlined',
                            fullWidth: true
                        }
                    }}
                />
            </FormControl>
            {/* End Date */}
            <FormControl variant="outlined" margin="dense" style={{ maxWidth: '220px' }}>
                <DatePicker
                    label={<Translate id="hospital.search_box.end_date" />}
                    value={endDate ? dayjs(endDate) : null}
                    onChange={(newDate) => setEndDate(newDate ? newDate.format('YYYY-MM-DD') : '')}
                    slotProps={{
                        textField: {
                            variant: 'outlined',
                            fullWidth: true
                        }
                    }}
                />
            </FormControl>
            {/* Clear Dates Button */}
            {(startDate || endDate) && (
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                        setStartDate("");
                        setEndDate("");
                    }}
                    style={{ 
                        height: '40px', // Match the height of the select dropdowns
                        minWidth: '100px',
                        textTransform: 'none'
                    }}
                >
                    <Translate id="hospital.search_box.clear_dates" />
                </Button>
            )}
        </div>
    );

    let filterItems:FilterItem[] = [
        {label:"draft", selected: documentStatusFilter.includes(DocumentStatus.DRAFT),  value:DocumentStatus.DRAFT, icon:documentStatusToIcon(DocumentStatus.DRAFT), color:documentStatusToColor(DocumentStatus.DRAFT), callBack:() => applyDocumentStatus(DocumentStatus.DRAFT)},                        
        {label:"closed", selected: documentStatusFilter.includes(DocumentStatus.CLOSED),  value:DocumentStatus.CLOSED, icon:documentStatusToIcon(DocumentStatus.CLOSED), color:documentStatusToColor(DocumentStatus.CLOSED), callBack:() => applyDocumentStatus(DocumentStatus.CLOSED)},                        
        {label:"paid", selected: statusPaidFilter.includes(BillStatus.PAID), value:BillStatus.PAID, icon:paidStatusToIcon(BillStatus.PAID), color:paidStatusToColor(BillStatus.PAID), callBack:() => applyStatusPaidFilter(BillStatus.PAID)},
        {label:"pending_payment", selected: statusPaidFilter.includes(BillStatus.PENDING_PAYMENT),  value:BillStatus.PENDING_PAYMENT, icon:paidStatusToIcon(BillStatus.PENDING_PAYMENT), color:paidStatusToColor(BillStatus.PENDING_PAYMENT), callBack:() => applyStatusPaidFilter(BillStatus.PENDING_PAYMENT)},
    ];

    // Create separate PEC (insurance) status filters
    let pecFilterItems:FilterItem[] = [];
    if (!hideInsuranceStatusFilter) {
        pecFilterItems = INSURANCE_CLAIM_STATUS_OPTIONS.map((option, index) => ({
            label: option.label, // Use the same translation key as the form
            selected: insuranceStatusFilter.includes(option.value as InsuranceClaimStatus),
            value: index,
            icon: insuranceClaimStatusToIcon(option.value as InsuranceClaimStatus),
            color: insuranceClaimStatusToColor(option.value as InsuranceClaimStatus),
            callBack: () => applyInsuranceStatusFilter(option.value as InsuranceClaimStatus)
        }));
    }
    if(canCreateBugdet){
        const bugetFilters:FilterItem[] = [
            {label:"summaries", selected: typeFilter.includes(DocumentType.SUMMARY),  value:DocumentType.INVOICE, icon:documentTypeToIcon(DocumentType.SUMMARY), color:documentTypeToColor(DocumentType.SUMMARY), callBack:() => applyTypeStatusFilter(DocumentType.SUMMARY)},
            {label:"budget", selected: typeFilter.includes(DocumentType.BUDGET), value:DocumentType.BUDGET, icon:documentTypeToIcon(DocumentType.BUDGET), color:documentTypeToColor(DocumentType.BUDGET), callBack:() => applyTypeStatusFilter(DocumentType.BUDGET)},
            {label:"invoices", selected: typeFilter.includes(DocumentType.INVOICE),  value:DocumentType.INVOICE, icon:documentTypeToIcon(DocumentType.INVOICE), color:documentTypeToColor(DocumentType.INVOICE), callBack:() => applyTypeStatusFilter(DocumentType.INVOICE)},                         
        ];
        filterItems = [...bugetFilters, ...filterItems];
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <SearchBox 
                    hideSearchPatient={hideSearchPatient}
                    textField={{label:"hospital.billing.search_patient", callBack:callbackNameTyped}}
                    filterItems={filterItems}
                    extraFilters={renderDateFilters}
                />
            </Grid>
            
            {/* Separate PEC (Insurance) Filters */}
            {!hideInsuranceStatusFilter && pecFilterItems.length > 0 && (
                <Grid item xs={12}>
                    <Paper elevation={3} style={{padding:"1rem", marginTop:'0.5rem'}} >
                        <Grid container spacing={2}>
                            <Grid item xs={12} container alignItems='center'>
                                <Grid item zeroMinWidth>
                                    <Typography variant="body2" component="span">
                                        <Translate id="hospital.search_box.pec_filter" />
                                    </Typography>:
                                    {
                                        pecFilterItems.map((item) => {
                                            return(
                                                <ChipContainer key={item.value}>
                                                    <ColourChip 
                                                        rgbcolor={item.color} 
                                                        active={!item.selected} 
                                                        avatar={item.icon}
                                                        onClick={item.callBack} 
                                                        label={<Translate id={item.label} />}
                                                    />
                                                </ChipContainer>
                                            );
                                        })
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            )}
            

            
            <Grid item xs={12}>
                {
                    renderCore()
                }
            </Grid>
        </Grid>
    );
};

export default BillsTable;
